import type { ModalitaPartecipazione } from '@aulaviva/shared';
import { Alert, Button, Card, Dialog, LiveBadge, PageHeading, useToast } from '@aulaviva/ui';
import { ArrowLeft } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useConfermaWebinar, useRinunciaWebinar, useWebinarPartecipante } from '@/api/percorsi';
import { formattaIntervallo } from '@/lib/date';
import { comuni, percorsi, test as tt } from '@/testi';
import { DialogoModalita } from './componenti/DialogoModalita';
import { StatoConferma } from './componenti/StatoConferma';

export function WebinarDettaglio() {
  const t = percorsi.dettaglio;
  const { id = '' } = useParams();
  const webinar = useWebinarPartecipante(id);
  const conferma = useConfermaWebinar(id);
  const rinuncia = useRinunciaWebinar(id);
  const toast = useToast();
  const navigate = useNavigate();
  const [dialogoModalita, setDialogoModalita] = useState(false);
  const [dialogoRinuncia, setDialogoRinuncia] = useState(false);

  if (webinar.isPending) return <p className="t-body text-ink-muted">{comuni.caricamento}</p>;
  if (webinar.isError) return <Alert variant="error" title={webinar.error.message} action={<Button onClick={() => webinar.refetch()}>{comuni.riprova}</Button>} />;
  const w = webinar.data;
  const c = w.conferma;
  const chiuso = w.stato === 'chiuso';
  const iniziato = w.stato === 'in_diretta' || w.stato === 'test_aperto' || chiuso;

  async function salvaModalita(modalita: ModalitaPartecipazione) {
    try {
      await conferma.mutateAsync(modalita);
      toast.mostra({ variant: 'success', title: t.salvato });
    } catch (e) {
      toast.mostra({ variant: 'error', title: e instanceof Error ? e.message : comuni.erroreGenerico });
      throw e;
    }
  }
  async function confermaRinuncia() {
    try {
      await rinuncia.mutateAsync();
      setDialogoRinuncia(false);
      toast.mostra({ variant: 'success', title: t.rinunciato });
    } catch (e) {
      toast.mostra({ variant: 'error', title: e instanceof Error ? e.message : comuni.erroreGenerico });
    }
  }

  const testoConferma = !c ? t.nonInvitato : c.stato === 'rinunciato' ? t.rinunciato : c.stato === 'confermato' ? (c.modalita === 'sala' ? t.confermatoSala : t.confermatoOnline) : t.invitato;

  return (
    <>
      <Link to="/" className="inline-flex items-center gap-2 t-label no-underline text-ink-muted hover:text-ink">
        <ArrowLeft aria-hidden size={20} strokeWidth={1.75} />
        {t.torna}
      </Link>
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-3 flex-wrap">
          {w.stato === 'in_diretta' && <LiveBadge />}
          <StatoConferma webinar={w} />
        </div>
        <PageHeading subtitle={w.descrizione ?? undefined}>{w.titolo}</PageHeading>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="flex flex-col gap-2">
          <h2 className="t-label text-ink-muted">{t.quando}</h2>
          <p className="t-body">{formattaIntervallo(w.inizioPrevisto, w.finePrevista)}</p>
          <p className="t-body-small text-ink-muted">{t.durata(w.durataUfficialeMin)}</p>
        </Card>
        <Card className="flex flex-col gap-2">
          <h2 className="t-label text-ink-muted">{t.relatori}</h2>
          {w.relatori.length === 0 ? (
            <p className="t-body text-ink-muted">{t.nessunRelatore}</p>
          ) : (
            <ul className="t-body flex flex-col gap-1">
              {w.relatori.map((r, i) => (
                <li key={i}>
                  {r.nome} {r.cognome}
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
      <Card className="flex flex-col gap-4">
        <h2 className="t-title-3">{t.laTuaConferma}</h2>
        <p className="t-body">{testoConferma}</p>
        {c && !iniziato && <p className="t-body-small text-ink-muted">{t.cambiaIdea}</p>}
        {c && !iniziato && (
          <div className="flex flex-wrap gap-3">
            {c.stato === 'invitato' && (
              <Button variant="primary" onClick={() => setDialogoModalita(true)}>
                {percorsi.azioni.conferma}
              </Button>
            )}
            {c.stato === 'confermato' && (
              <>
                <Button onClick={() => setDialogoModalita(true)}>{t.cambiaModalita}</Button>
                <Button variant="discreet" onClick={() => setDialogoRinuncia(true)}>
                  {t.rinuncia}
                </Button>
              </>
            )}
            {c.stato === 'rinunciato' && (
              <Button variant="primary" onClick={() => setDialogoModalita(true)}>
                {t.riconferma}
              </Button>
            )}
          </div>
        )}
        {c?.stato === 'confermato' && (w.stato === 'test_aperto' || chiuso) && (
          <Alert variant="info" title={tt.titolo} action={<Button variant="primary" onClick={() => navigate(`/webinar/${id}/test`)}>{tt.azioni.fai}</Button>} />
        )}
        {c?.stato === 'confermato' && (w.eOggi || w.stato === 'in_diretta') && !chiuso && (
          <Alert
            variant="info"
            title={w.stato === 'in_diretta' ? percorsi.azioni.entra : t.aulaPresto}
            action={
              <Button variant={w.stato === 'in_diretta' ? 'primary' : 'secondary'} onClick={() => navigate(`/webinar/${id}/aula`)}>
                {percorsi.azioni.entra}
              </Button>
            }
          />
        )}
      </Card>
      {dialogoModalita && (
        <DialogoModalita open onOpenChange={setDialogoModalita} attuale={c?.modalita} onConferma={salvaModalita} />
      )}
      <Dialog
        open={dialogoRinuncia}
        onOpenChange={setDialogoRinuncia}
        title={t.rinunciaTitolo}
        description={t.rinunciaTesto}
        actions={
          <>
            <Button onClick={() => setDialogoRinuncia(false)}>{percorsi.dialogo.annulla}</Button>
            <Button variant="primary" onClick={confermaRinuncia} loading={rinuncia.isPending} loadingText={percorsi.dialogo.inCorso}>
              {t.rinunciaConferma}
            </Button>
          </>
        }
      />
    </>
  );
}
